// src/pages/PrivacyPolicy.jsx
import React from "react";
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <main className="min-h-screen text-indigo-100">
      <Helmet>
        <title>Privacy Policy — Andlegur</title>
        <meta name="description" content="Read Andlegur's Privacy Policy. Learn how we collect, use, and protect your personal information across our websites and applications." />
        <link rel="canonical" href="https://andlegur.com/privacy" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Header */}
      <section className="border-b border-indigo-900/40 bg-[#060B20]">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <h1 className="text-3xl font-semibold tracking-tight text-indigo-50">
            Privacy Policy — Andlegur
          </h1>
          <p className="mt-2 text-sm text-indigo-300">
            Last updated: <span className="font-medium text-indigo-200">February 23 2026</span>
          </p>
          <p className="mt-6 text-indigo-100">
            This Privacy Policy explains how Andlegur (“Andlegur,” “we,” “us,” or “our”) collects,
            uses, shares, and safeguards personal information when you access our websites,
            applications, companion experiences, and related services (collectively, the “Services”).
            By using the Services, you acknowledge this Privacy Policy and{" "}
            <Link to="/terms" className="text-indigo-300 underline hover:text-indigo-100">
              our Terms of Service
            </Link>.
          </p>
          <p className="mt-4 text-indigo-50">
            This Policy is designed for compliance with applicable laws including, where relevant,
            the EU/EEA General Data Protection Regulation (“GDPR”), the UK GDPR, the California
            Consumer Privacy Act as amended by the CPRA (“CCPA/CPRA”), and Canada’s Personal
            Information Protection and Electronic Documents Act (“PIPEDA”).
          </p>
          <div className="mt-6">
            <Link
              to="/"
              className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="mx-auto max-w-4xl px-6 pt-12">
        <nav
          aria-label="Table of contents"
          className="mb-10 rounded-xl border border-indigo-900/40 bg-[#0A1030] p-6"
        >
          <h2 className="text-lg font-semibold text-indigo-50">Table of Contents</h2>
          <ol className="mt-4 grid gap-2 text-sm text-indigo-100 sm:grid-cols-2">
            {[
              ["Scope & Controller", "scope"],
              ["Information We Collect", "collect"],
              ["Sources of Personal Information", "sources"],
              ["How We Use Information (Purposes)", "purposes"],
              ["Lawful Bases (EEA/UK)", "lawful-bases"],
              ["AI, Profiling & Automated Decisions", "ai"],
              ["Cookies & Tracking Technologies", "cookies"],
              ["Sharing & Disclosures to Third Parties", "sharing"],
              ["International Data Transfers", "transfers"],
              ["Data Retention", "retention"],
              ["Security Measures", "security"],
              ["Your Rights: GDPR/UK GDPR", "rights-eu"],
              ["Your Rights: California (CPRA/CCPA)", "rights-ca"],
              ["Your Rights: Canada (PIPEDA)", "rights-ca-pipeda"],
              ["Children’s Privacy", "children"],
              ["Marketing, Communications & Preferences", "marketing"],
              ["Do Not Track & Global Privacy Control", "dnt-gpc"],
              ["User Responsibilities & Liability", "responsibilities"],
              ["Refund & Purchase Policy", "refund-policy"],
              ["Exercising Your Rights", "requests"],
              ["Changes to this Policy", "changes"],
              ["Contact, DPO & Representatives", "contact"],
            ].map(([label, id]) => (
              <li key={id}>
                <a href={`#${id}`} className="hover:text-indigo-100 hover:underline">
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-4xl px-6 pb-16">
  <article className="prose prose-invert prose-indigo max-w-none [&_section]:my-12 [&_h2]:text-xl [&_h2]:mb-2 [&_ul]:mt-2">
          <section id="scope" className="scroll-mt-24">
            <h2>1. Scope & Controller</h2>
            <p>
              This Policy applies to personal information processed by Andlegur in connection with
              the Services worldwide. If we act as a processor on behalf of a
              partner, the partner’s privacy terms may also apply.
            </p>
          </section>

          <section id="collect" className="scroll-mt-24">
            <h2>2. Information We Collect</h2>
            <p>We collect the following categories of information, depending on your use:</p>
            <ul>
              <li>
                <strong>Account & Identity Data:</strong> name, username, email address,
                authentication identifiers, profile photo (optional).
              </li>
              <li>
                <strong>Profile & Preference Data:</strong> archetype selections, ritual settings,
                language, time zone, notification preferences.
              </li>
              <li>
                <strong>Content You Provide:</strong> journal entries, reflections, messages,
                prompts, goals, check-ins, community posts. You choose what to share.
              </li>
              <li>
                <strong>Usage & Interaction Data:</strong> features used, pages viewed, session
                metadata, clickstream, approximate location (derived from IP), performance metrics.
              </li>
              <li>
                <strong>Device & Technical Data:</strong> device type, OS, browser, IP address,
                app version, cookie IDs, mobile IDs, diagnostics.
              </li>
              <li>
                <strong>Payment & Transaction Data:</strong> subscription plan, billing details,
                transaction identifiers and status (processed via secure third-party processors).
              </li>
              <li>
                <strong>Support & Communications:</strong> inquiries, feedback, and related
                correspondence.
              </li>
              <li>
                <strong>Inferences:</strong> insights derived from your activity to personalize
                guidance, content, and experiences.
              </li>
            </ul>
            <p>
              We do not intentionally collect sensitive personal data (e.g., health, biometric, or
              precise geolocation) unless you knowingly provide it as part of your reflective use,
              and only where permitted by law and necessary for the Services you request.
            </p>
          </section>

          <section id="sources" className="scroll-mt-24">
            <h2>3. Sources of Personal Information</h2>
            <ul>
              <li><strong>Directly from you</strong> when you create an account, submit content, or contact support.</li>
              <li><strong>Automatically</strong> via cookies, SDKs, pixels, and similar technologies when you use the Services.</li>
              <li><strong>From third parties</strong> such as analytics providers, payment processors, authentication services, and referral partners.</li>
            </ul>
          </section>

          <section id="purposes" className="scroll-mt-24">
            <h2>4. How We Use Information (Purposes)</h2>
            <ul>
              <li>Provide, operate, and secure the Services.</li>
              <li>Personalize journeys, rituals, prompts, archetype interactions, and content.</li>
              <li>Measure performance, conduct analytics, and improve features.</li>
              <li>Process payments, subscriptions, and account management.</li>
              <li>Prevent fraud, abuse, and security incidents.</li>
              <li>Comply with legal obligations and enforce our Terms of Service.</li>
              <li>Communicate about updates, new features, and relevant offerings (with opt-out choices).</li>
              <li>Create aggregated or de-identified data for research and product development.</li>
            </ul>
          </section>

          <section id="lawful-bases" className="scroll-mt-24">
            <h2>5. Lawful Bases (EEA/UK)</h2>
            <p>Where GDPR/UK GDPR applies, we rely on one or more of the following lawful bases:</p>
            <ul>
              <li><strong>Contract:</strong> to provide and support the Services you request.</li>
              <li><strong>Legitimate Interests:</strong> to secure, improve, and grow the Services, balanced against your rights.</li>
              <li><strong>Consent:</strong> for certain personalization, cookies, marketing, or when you choose to share sensitive reflections.</li>
              <li><strong>Legal Obligation:</strong> to comply with applicable laws and regulatory requests.</li>
            </ul>
          </section>

          <section id="ai" className="scroll-mt-24">
            <h2>6. AI Features, Third-Party AI Processing & Automated Analysis</h2>
            <p>
              Andlegur's AI companion, daily insight, and emotion analysis features are powered by
              <strong> OpenAI, LLC</strong> ("OpenAI"). To operate these features, the following user-generated content is
              transmitted to OpenAI's API servers:
            </p>
            <ul>
              <li>
                <strong>AI Companion chat:</strong> Your current message, up to four recent messages
                from the same session (truncated to 150 characters each), and your detected emotional
                state (e.g., "expressing Grateful") are sent to generate a personalized reply.
              </li>
              <li>
                <strong>Daily Insights:</strong> Excerpts from up to 50 recent chat messages (150
                characters each) and up to 7 recent journal entries (200 characters each), plus
                derived emotional themes and key topics, are sent to generate a personalized daily
                insight.
              </li>
              <li>
                <strong>Emotion Analysis:</strong> The raw text of each message you send is
                independently analyzed by OpenAI to detect your emotional state and tag responses.
              </li>
            </ul>
            <p>
              <strong>No personal identifiers</strong> — such as your name, email address, or account
              ID — are included in the data sent to OpenAI. Only the content text and app-level
              context (archetype name, session tone) are transmitted.
            </p>
            <p>
              OpenAI processes this data as a <strong>data processor on behalf of Andlegur</strong> under OpenAI's API Data Processing Addendum. OpenAI does not use API-submitted data to
              train or improve its models. For more information, see{" "}
              <a
                href="https://openai.com/policies/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-300 underline hover:text-indigo-100"
              >
                OpenAI's Privacy Policy
              </a>.
            </p>
            <p>
              By using AI-powered features (chat companion, daily insights, emotion analysis), you
              consent to your message content and journal excerpts being transmitted to OpenAI for
              processing as described above. You acknowledge this at the end of the onboarding
              process before first use. You may decline to use these AI features at any time;
              journaling, rituals, and community features do not require data to be sent to OpenAI.
            </p>
            <p>
              We may also build non-marketing preference profiles to personalize your experience
              using data stored within our own systems. We do not conduct automated decision-making
              that produces legal effects about you without human involvement.
            </p>
          </section>

          <section id="cookies" className="scroll-mt-24">
            <h2>7. Cookies & Tracking Technologies</h2>
            <p>
              We and our partners use cookies, pixels, SDKs, and local storage to enable core
              functionality, remember preferences, measure usage, and improve performance. Where
              legally required, we request consent for non-essential cookies. You can manage
              preferences in our cookie banner (if implemented) and via your browser settings.
            </p>
          </section>

          <section id="sharing" className="scroll-mt-24">
            <h2>8. Sharing & Disclosures to Third Parties</h2>
            <p>We may disclose personal information to the following categories of recipients:</p>
            <ul>
              <li>
                <strong>AI Processing — OpenAI, LLC:</strong> Message content, journal entry excerpts,
                and derived emotional data are transmitted to OpenAI to power the AI companion, daily
                insights, and emotion analysis features. See Section 6 for full details. OpenAI is
                bound by a Data Processing Addendum and does not use API data for model training.
              </li>
              <li><strong>Database & Authentication — Supabase:</strong> your account data, content, and session data are stored and managed via Supabase's hosted infrastructure.</li>
              <li><strong>Payment Processing — Stripe, Inc.:</strong> billing details and transaction data are handled by Stripe under their own privacy policy and PCI-DSS compliance.</li>
              <li><strong>Service Providers/Processors:</strong> hosting, cloud infrastructure (Render, Vercel), analytics, customer support, messaging, and error tracking (e.g., Sentry).</li>
              <li><strong>Integration Partners:</strong> optional connections you enable.</li>
              <li><strong>Professional Advisors:</strong> legal, compliance, and accounting.</li>
              <li><strong>Authorities & Legal:</strong> to comply with law or protect rights, safety, and security.</li>
              <li><strong>Business Transfers:</strong> in connection with mergers, acquisitions, or asset sales, subject to safeguards.</li>
            </ul>
            <p>
              We do not sell your personal information in the conventional sense. Under CPRA, we may
              “share” limited identifiers or usage data for cross-context behavioral advertising
              where permitted and with your consent where required. You may opt out of such sharing
              using available controls.
            </p>
          </section>

          <section id="transfers" className="scroll-mt-24">
            <h2>9. International Data Transfers</h2>
            <p>
              We operate globally. Your information may be transferred to and processed in countries
              outside your residence. Where required, we use appropriate safeguards such as Standard
              Contractual Clauses, UK IDTA/Addendum, and supplementary measures to protect data
              during international transfers.
            </p>
          </section>

          <section id="retention" className="scroll-mt-24">
            <h2>10. Data Retention</h2>
            <p>
              We retain personal information only as long as necessary for the purposes described,
              including providing the Services, complying with legal obligations, resolving disputes,
              and enforcing agreements. Retention periods vary by data type and context. We may
              retain aggregated or de-identified data for research and improvement.
            </p>
            
            <h3 className="text-lg font-semibold text-indigo-50 mt-6 mb-2">Account Deletion</h3>
            <p>
              You have the right to request deletion of your account and associated personal data at any time through the app's account settings (Profile → Security → Delete Account). When you initiate account deletion:
            </p>
            <ul>
              <li>
                <strong>Immediate Access Revocation:</strong> Your access to the Services will be revoked immediately upon confirming the deletion request. You will be signed out and unable to access your account.
              </li>
              <li>
                <strong>45-Day Grace Period:</strong> Your account and data will be marked for deletion but retained for 45 days. During this period, you may contact support@andlegur.com to cancel the deletion request and recover your account.
              </li>
              <li>
                <strong>Permanent Deletion:</strong> After the 45-day grace period expires, your account and all associated personal data will be permanently deleted from our systems. This includes:
                <ul className="ml-6 mt-2">
                  <li>Profile information (name, email, bio, avatar)</li>
                  <li>All reflections, posts, and content you created</li>
                  <li>Circle memberships and community interactions</li>
                  <li>Chat messages and conversations</li>
                  <li>Archetype assessments and insights</li>
                  <li>Subscription and payment history</li>
                  <li>Device and session information</li>
                  <li>Notification preferences and settings</li>
                </ul>
              </li>
              <li>
                <strong>Non-Recoverable:</strong> Once the permanent deletion is executed, your data cannot be recovered. This action is irreversible.
              </li>
              <li>
                <strong>Legal Retention:</strong> We may retain certain information where required by law (e.g., financial records for tax purposes, security logs for fraud investigation) or to protect our legal rights, even after account deletion.
              </li>
              <li>
                <strong>Aggregated Data:</strong> Anonymized, aggregated, or de-identified data derived from your usage may be retained for research, analytics, and service improvement purposes. This data cannot be used to identify you personally.
              </li>
            </ul>
            <p className="mt-4">
              <strong>Important:</strong> If you have an active paid subscription, deleting your account does not automatically cancel your subscription or entitle you to a refund. Please cancel your subscription before deleting your account if you wish to avoid future charges. See Section 19 (Refund & Purchase Policy) for details.
            </p>
          </section>

          <section id="security" className="scroll-mt-24">
            <h2>11. Security Measures</h2>
            <p>
              We implement administrative, technical, and physical safeguards appropriate to the
              nature of the data we process. No system is completely secure, and we cannot guarantee
              absolute security. You are responsible for maintaining the confidentiality of your
              account credentials and for promptly notifying us of any suspected compromise.
            </p>
          </section>

          <section id="rights-eu" className="scroll-mt-24">
            <h2>12. Your Rights: GDPR/UK GDPR</h2>
            <p>
              Subject to eligibility and exemptions, you may have the right to access, rectify, erase,
              restrict, object to processing (including profiling), and data portability, and the
              right to withdraw consent at any time. You also have the right to lodge a complaint
              with your local supervisory authority.
            </p>
          </section>

          <section id="rights-ca" className="scroll-mt-24">
            <h2>13. Your Rights: California (CPRA/CCPA)</h2>
            <p>
              California residents may have rights to know/access specific pieces and categories of
              personal information collected, the purposes, and categories of recipients; to delete
              personal information; to correct inaccuracies; to opt out of selling/sharing;
              to limit the use and disclosure of sensitive personal information; and to be free from
              discrimination for exercising these rights.
            </p>
            <p>
              Categories of information we may collect include identifiers, commercial information,
              internet/usage data, approximate geolocation (from IP), inferences, and user-provided
              content. We do not use sensitive personal information for inferring characteristics or
              for purposes other than those allowed by law without your consent.
            </p>
          </section>

          <section id="rights-ca-pipeda" className="scroll-mt-24">
            <h2>14. Your Rights: Canada (PIPEDA)</h2>
            <p>
              Canadian residents may request access to, correction of, or deletion of personal
              information, and may challenge our compliance with PIPEDA. Where consent is required,
              you may withdraw consent subject to legal or contractual limitations and reasonable
              notice.
            </p>
          </section>

          <section id="children" className="scroll-mt-24">
            <h2>15. Children’s Privacy</h2>
            <p>
              The Services are not directed to individuals under the age of 16. We do not knowingly
              collect personal information from children. If we learn we have collected such
              information, we will take reasonable steps to delete it and terminate the account.
            </p>
          </section>

          <section id="marketing" className="scroll-mt-24">
            <h2>16. Marketing, Communications & Preferences</h2>
            <p>
              We may send service-related communications, including security alerts and transactional
              messages. With your consent where required, we may send promotional messages about
              features, events, and offerings. You can opt out of promotional communications at any
              time by following unsubscribe instructions or adjusting your preferences in the app.
              We may still send non-promotional, service-related messages.
            </p>
          </section>

          <section id="dnt-gpc" className="scroll-mt-24">
            <h2>17. Do Not Track & Global Privacy Control</h2>
            <p>
              Some browsers offer “Do Not Track” signals; there is no consensus on how to respond,
              and our Services may not act on DNT signals. Where legally required and technically
              feasible, we will honor Global Privacy Control (GPC) signals as an opt out of
              selling/sharing for cross-context behavioral advertising.
            </p>
          </section>

          <section id="responsibilities" className="scroll-mt-24">
            <h2>18. User Responsibilities & Liability</h2>
            <p>
              <strong>You are fully responsible for all content, data, and information you provide, upload, or share through the Services.</strong> This includes but is not limited to:
            </p>
            <ul>
              <li>
                <strong>Account Security:</strong> You are solely responsible for maintaining the confidentiality of your account credentials (username, password, authentication methods). You must immediately notify us of any unauthorized access or security breach.
              </li>
              <li>
                <strong>Content Accuracy:</strong> You warrant that all information you provide is accurate, complete, and current. You are responsible for updating your information as needed.
              </li>
              <li>
                <strong>Third-Party Rights:</strong> You must not upload, share, or transmit any content that violates the intellectual property rights, privacy rights, or other rights of third parties without proper authorization.
              </li>
              <li>
                <strong>Prohibited Content:</strong> You agree not to share content that is unlawful, defamatory, harassing, abusive, fraudulent, obscene, or otherwise objectionable. You must not share sensitive personal data of third parties without lawful basis and their explicit consent.
              </li>
              <li>
                <strong>Voluntary Disclosure:</strong> Any reflective, personal, or sensitive content you choose to share is at your own discretion and risk. We provide tools for privacy settings, but you are responsible for managing what you share and with whom.
              </li>
              <li>
                <strong>Community Guidelines:</strong> You must comply with our{" "}
                <Link to="/terms" className="text-indigo-300 underline hover:text-indigo-100">
                  Terms of Service
                </Link>{" "}
                and community guidelines at all times. Violations may result in account suspension or termination.
              </li>
              <li>
                <strong>Legal Compliance:</strong> You are responsible for ensuring your use of the Services complies with all applicable local, state, national, and international laws and regulations.
              </li>
              <li>
                <strong>Assumption of Risk:</strong> You acknowledge and accept all risks associated with using the Services, including but not limited to risks related to data transmission, content sharing, and interactions with other users or AI-powered features.
              </li>
            </ul>
            <p>
              <strong>BY USING THE SERVICES, YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS ANDLEGUR, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND PARTNERS FROM ANY CLAIMS, LOSSES, DAMAGES, LIABILITIES, AND EXPENSES (INCLUDING LEGAL FEES) ARISING FROM OR RELATED TO YOUR USE OF THE SERVICES, YOUR CONTENT, YOUR VIOLATION OF THIS POLICY OR OUR TERMS, OR YOUR VIOLATION OF ANY RIGHTS OF THIRD PARTIES.</strong>
            </p>
          </section>

          <section id="refund-policy" className="scroll-mt-24">
            <h2>19. Refund & Purchase Policy</h2>
            <p>
              This section outlines our policies regarding refunds and purchases made through the Services, including subscriptions and physical products.
            </p>
            
            <h3 className="text-lg font-semibold text-indigo-50 mt-6 mb-2">Subscription Services - No Refund Policy</h3>
            <p>
              <strong>ALL SUBSCRIPTION PURCHASES ARE FINAL AND NON-REFUNDABLE.</strong> We operate a strict no-refund policy for all subscription tiers, including but not limited to monthly, annual, or promotional subscription plans.
            </p>
            <ul>
              <li>
                <strong>Free Trial Period:</strong> We offer free tier options that allow you to explore and evaluate the Services before committing to a paid subscription. By choosing to upgrade to a paid tier, you acknowledge that you have had the opportunity to assess the Services and make an informed decision.
              </li>
              <li>
                <strong>Your Responsibility:</strong> Once you subscribe to a paid tier, you assume full responsibility for that purchase. You acknowledge and agree that:
                <ul className="ml-6 mt-2">
                  <li>You have reviewed the subscription features, benefits, and pricing before purchasing.</li>
                  <li>You understand the subscription terms, including billing cycles, renewal dates, and cancellation policies.</li>
                  <li>You will not be entitled to any refund, credit, or compensation for any reason, including but not limited to dissatisfaction with features, change of mind, technical issues, or unused portions of your subscription period.</li>
                </ul>
              </li>
              <li>
                <strong>Continued Access:</strong> Upon payment, you will have access to all features and benefits included in your subscription tier until the end of your current billing period. If you cancel your subscription, you will retain access until the end of the period you have already paid for, but no refund will be issued for the remaining time.
              </li>
              <li>
                <strong>No Pressure to Purchase:</strong> We do not employ high-pressure sales tactics, false advertising, or misleading promotional materials. All subscription features, limitations, and pricing are clearly disclosed. By subscribing, you confirm that you were not coerced or misled into making the purchase.
              </li>
              <li>
                <strong>Cancellation:</strong> You may cancel your subscription at any time through your account settings. Cancellation will prevent future billing but does not entitle you to a refund for the current or any prior billing periods.
              </li>
              <li>
                <strong>Exceptions:</strong> Refunds may only be provided in cases where required by applicable law or where Andlegur, at its sole discretion, determines that exceptional circumstances warrant a refund (e.g., technical error resulting in duplicate charges, unauthorized access to your payment method). Any such refund requests must be submitted in writing to support@andlegur.com with detailed explanation and supporting evidence.
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-indigo-50 mt-6 mb-2">Physical Products (Shop) - No Refund Policy</h3>
            <p>
              <strong>ALL PHYSICAL PRODUCT PURCHASES ARE FINAL AND NON-REFUNDABLE.</strong> We operate a strict no-refund policy for all items purchased through our shop or marketplace.
            </p>
            <ul>
              <li>
                <strong>Informed Purchase:</strong> You are solely responsible for reviewing product descriptions, specifications, images, prices, and shipping information before making a purchase. By completing a purchase, you confirm that:
                <ul className="ml-6 mt-2">
                  <li>You have carefully reviewed the product details and are satisfied with your selection.</li>
                  <li>You understand what you are purchasing and accept the product "as is" based on the description provided.</li>
                  <li>You were not pressured, coerced, or misled into making the purchase.</li>
                </ul>
              </li>
              <li>
                <strong>No False Promises:</strong> Nowhere in the Services, app, website, marketing materials, or any related sources do we promote refunds, guarantees, or suggest that purchases are reversible. All sales are explicitly final.
              </li>
              <li>
                <strong>Shipping & Delivery Responsibility:</strong> We take full responsibility for ensuring that your purchased items are properly packaged and shipped to the delivery address you provide. However:
                <ul className="ml-6 mt-2">
                  <li>
                    <strong>Delivery Partners:</strong> We partner with third-party courier and delivery services to fulfill shipments. Once an item leaves our facility, we are not responsible for delays, loss, damage, or non-delivery caused by the delivery partner, courier service, or circumstances beyond our control (e.g., natural disasters, customs issues, incorrect address, recipient unavailability).
                  </li>
                  <li>
                    <strong>Address Accuracy:</strong> You are fully responsible for providing a complete, accurate, and deliverable shipping address at the time of purchase. Once an item has been shipped, we cannot change the delivery address. If an item is undeliverable due to an incorrect, incomplete, or invalid address you provided, you will not be entitled to a refund or replacement.
                  </li>
                  <li>
                    <strong>Tracking & Communication:</strong> We will provide tracking information where available. It is your responsibility to monitor the delivery status and coordinate with the delivery partner if needed.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Collaboration for Resolution:</strong> While we maintain a no-refund policy, we are committed to customer satisfaction. If you experience issues with delivery (e.g., lost package, damaged goods, non-delivery despite confirmed shipment), please contact us at support@andlegur.com. We will work with you and our delivery partners to investigate and resolve the issue to the best of our ability, which may include:
                <ul className="ml-6 mt-2">
                  <li>Filing a claim with the delivery partner.</li>
                  <li>Requesting proof of delivery or investigating delivery status.</li>
                  <li>In rare cases, at our sole discretion, offering a replacement product (subject to availability and verification of the issue).</li>
                </ul>
                <p className="mt-2">
                  However, such collaboration does not constitute a guarantee of refund or replacement. Any resolution will be determined on a case-by-case basis at Andlegur's sole discretion.
                </p>
              </li>
              <li>
                <strong>No Returns or Exchanges:</strong> We do not accept returns or exchanges for any reason, including but not limited to change of mind, dissatisfaction with the product, buyer's remorse, or incorrect size/color selection (unless the error was on our part in fulfilling the order).
              </li>
              <li>
                <strong>Defective or Damaged Products:</strong> If you receive a product that is defective or damaged upon arrival (not due to delivery mishandling), please contact us within 7 days of delivery with photographic evidence. We will review the claim and, at our sole discretion, may offer a replacement or store credit. This does not constitute a refund policy.
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-indigo-50 mt-6 mb-2">General Terms</h3>
            <ul>
              <li>
                <strong>No Chargebacks:</strong> By making a purchase, you agree not to initiate chargebacks or payment disputes through your payment provider unless there is clear evidence of unauthorized use of your payment method or fraudulent charges. Initiating a chargeback in violation of this agreement may result in account suspension or termination.
              </li>
              <li>
                <strong>Legal Requirements:</strong> In jurisdictions where local consumer protection laws mandate refunds or cooling-off periods, those laws will apply to the extent required, and this policy will be interpreted in compliance with such laws. If any provision of this refund policy is deemed unenforceable under applicable law, the remainder of the policy will remain in full effect.
              </li>
              <li>
                <strong>Contact Us:</strong> If you have questions or concerns about a purchase, subscription, or delivery, please contact us at support@andlegur.com. We will respond in a timely manner and work with you to address your concerns within the scope of this policy.
              </li>
            </ul>
            <p className="mt-4">
              <strong>BY MAKING A PURCHASE OR SUBSCRIBING TO THE SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO THIS REFUND & PURCHASE POLICY. YOU ACCEPT FULL RESPONSIBILITY FOR YOUR PURCHASING DECISIONS AND WAIVE ANY CLAIMS FOR REFUNDS EXCEPT AS EXPLICITLY STATED ABOVE OR REQUIRED BY LAW.</strong>
            </p>
          </section>

          <section id="requests" className="scroll-mt-24">
            <h2>20. Exercising Your Rights</h2>
            <p>
              To exercise privacy rights or submit a request (access, deletion, correction, export,
              opt-out), contact us using the details below. We will verify your identity before
              fulfilling requests and respond within the timelines required by applicable law. You
              may authorize an agent where permitted, subject to verification.
            </p>
          </section>

          <section id="changes" className="scroll-mt-24">
            <h2>21. Changes to this Policy</h2>
            <p>
              We may update this Policy from time to time to reflect changes in our practices or
              legal requirements. The “Last updated” date indicates the latest revision. Material
              changes will be announced via the Services or by direct notice where appropriate.
              Continued use of the Services after an update constitutes acceptance of the revised
              Policy.
            </p>
          </section>

          <section id="contact" className="scroll-mt-24">
            <h2>22. Contact, DPO & Representatives</h2>
            <p>
              If you have questions about this Policy or our data practices, or wish to exercise
              your rights:
            </p>
            <p>
              Email: support@andlegur.com
            
            </p>

          </section>
        </article>

        {/* Footer Links */}
        <div className="mt-12 border-t border-indigo-900/40 pt-8 flex flex-wrap items-center gap-4">
          <Link
            to="/terms"
            className="inline-flex items-center rounded-lg bg-white/5 px-4 py-2 text-sm font-medium text-indigo-100 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          >
            View Terms of Service
          </Link>
          <Link
            to="/"
            className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          >
            Back to Home
          </Link>
        </div>

      </section>
    </main>
  );
}
